import { uniq } from "lodash";
import { MIXED } from "shared/rsDictionary";
import { getTimePartsFromSeconds } from "utils/dateTime";
import { isNotEmpty } from "utils/validate";
import { ELEMENTS_TYPE_LIST } from "./results.utils";
import { Accuracy, CalculationRowField, CalculationStatusSource, ResultSetElementType, RunsCount, runsCountValue, } from "../rs-workspace.types";
export const CALCULATION_ROW_DEFAULTS = {
    [CalculationRowField.uncertainty]: RunsCount.None,
    [CalculationRowField.variability]: RunsCount.None,
    [CalculationRowField.numberOfOptimisationSteps]: RunsCount.Low,
};
export const getResultElementNameAppearance = (element) => `${element.id}. ${{
    ...ELEMENTS_TYPE_LIST,
    [ResultSetElementType.Unknown]: { type: ResultSetElementType.Unknown, text: "" },
}[element.type]?.text}${element.name ? ` - ${element.name}` : ""}`;
export const getTotalRuns = ({ uncertainty, variability, numberOfOptimisationSteps, }, isProcessDataSource) => {
    if (isProcessDataSource) {
        return runsCountValue[RunsCount.None];
    }
    if (numberOfOptimisationSteps !== null) {
        return (runsCountValue[uncertainty] * runsCountValue[variability] * runsCountValue[numberOfOptimisationSteps]);
    }
    return runsCountValue[uncertainty] * runsCountValue[variability];
};
export const getPredictionTime = (predictionTime) => {
    if (predictionTime === undefined) {
        return "";
    }
    const { h, m } = getTimePartsFromSeconds(predictionTime);
    let prediction = "";
    if (predictionTime <= 60) {
        prediction = "1 min";
    }
    else if (predictionTime > 60 && predictionTime <= 600) {
        prediction = "10 min";
    }
    else if (predictionTime > 600 && predictionTime < 3000) {
        const min = Math.round(m / 10) * 10;
        prediction = `${min} min`;
    }
    else if (predictionTime >= 3000 && predictionTime < 86400) {
        const hr = h + (m >= 30 ? 1 : 0);
        prediction = `${hr} hr`;
    }
    else {
        const days = Math.round(predictionTime / 86400);
        prediction = `${days} day${days > 1 ? "s" : ""}`;
    }
    return prediction ? `~ ${prediction}` : "";
};
export const getAccuracyValue = (uncertainty, variability) => {
    if (uncertainty === undefined || variability === undefined) {
        return { label: Accuracy.NONE, level: 0 };
    }
    switch (uncertainty) {
        case RunsCount.None:
        case RunsCount.Tech3:
            return { level: 1, label: Accuracy.LOW };
        case RunsCount.Low:
            return { level: 2, label: Accuracy.MEDIUM };
        case RunsCount.Medium:
            switch (variability) {
                case RunsCount.None:
                case RunsCount.Tech3:
                    return { level: 2, label: Accuracy.MEDIUM };
                case RunsCount.Low:
                case RunsCount.Medium:
                    return { level: 3, label: Accuracy.HIGH };
                case RunsCount.High:
                    return { level: 4, label: Accuracy.VERY_HIGH };
            }
        // eslint-disable-next-line no-fallthrough
        case RunsCount.High: {
            switch (variability) {
                case RunsCount.None:
                case RunsCount.Tech3:
                    return { level: 2, label: Accuracy.MEDIUM };
                case RunsCount.Low:
                    return { level: 3, label: Accuracy.HIGH };
                case RunsCount.Medium:
                case RunsCount.High:
                    return { level: 4, label: Accuracy.VERY_HIGH };
            }
        }
    }
};
export const getCalculationElementTotalSettingsValue = (element, field) => {
    const aggregatedValues = uniq((field === "numberOfOptimisationSteps"
        ? element.rows.filter(({ sourceType }) => sourceType === "Optimization")
        : element.rows).map((row) => row[field] ?? CALCULATION_ROW_DEFAULTS[field]));
    return aggregatedValues.length === 1 ? aggregatedValues[0] : MIXED;
};
export const prepareCalculationElements = (calculations, preparedResultSets) => {
    const sourceGroups = [];
    const preparedElements = calculations.elements.reduce((acc, { resultElementId, resultSetId, rows, position }) => {
        const resultSet = preparedResultSets.find(({ id }) => id === resultSetId) || {};
        const resultSetElementIndex = resultSet.elements?.findIndex(({ id }) => id === resultElementId) ?? -1;
        const resultSetElement = resultSetElementIndex === -1
            ? {}
            : resultSet.elements?.[resultSetElementIndex];
        const resultElementName = `${resultSet.name} - ${getResultElementNameAppearance(resultSetElement)}`;
        const preparedElement = {
            resultElementId,
            resultSetId,
            position,
            name: resultElementName,
            type: resultSetElement.type,
            rows: rows
                .map((source) => {
                const { sourceId, sourceName, sourceType, isCompleted, sourceVariable, sourceUnit, sourceTiming, } = resultSetElement.sources?.find(({ id }) => id === source.responseId) ||
                    {};
                const preparedSource = {
                    ...source,
                    name: sourceName || sourceType,
                    sourceVariable,
                    sourceUnit,
                    sourceTiming,
                    sourceType,
                    sourceId: sourceId ?? undefined,
                    isCompleted: isCompleted && resultSetElement.isCompleted,
                };
                const sourceGroupIndex = sourceGroups.findIndex((group) => group.findIndex((groupedSource) => groupedSource.sourceId === sourceId && groupedSource.sourceType === sourceType) !== -1);
                const grouppedSource = {
                    sourceId: preparedSource.sourceId,
                    sourceType: preparedSource.sourceType,
                    uncertainty: preparedSource.uncertainty,
                    variability: preparedSource.variability,
                    resultSetId,
                    resultElementId,
                    resultElementName,
                };
                if (sourceGroupIndex === -1) {
                    sourceGroups.push([grouppedSource]);
                }
                else {
                    sourceGroups[sourceGroupIndex].push(grouppedSource);
                }
                return preparedSource;
            })
                .sort((a, b) => a.position - b.position)
                .map((row, index) => ({ ...row, position: index + 1 })),
        };
        return [...acc, preparedElement];
    }, []);
    const forceAccuracyData = sourceGroups
        .filter((group) => group.length > 1)
        .map((group) => group.reduce((acc, source) => {
        const { uncertainty, variability, ...restSource } = source;
        const accuracy = getAccuracyValue(uncertainty, variability);
        if (!acc?.accuracy?.level || acc?.accuracy?.level < accuracy.level) {
            return { ...restSource, accuracy };
        }
        return acc;
    }, {}));
    return preparedElements
        .map((element) => {
        const rows = element.rows.map((row) => {
            const forcedAccuracyItem = forceAccuracyData.find((item) => item.sourceId === row.sourceId && item.sourceType === row.sourceType);
            if (forcedAccuracyItem) {
                row.forcedAccuracy = forcedAccuracyItem;
            }
            return row;
        });
        return { ...element, rows };
    })
        .sort((a, b) => a.position - b.position)
        .map((element, index) => ({ ...element, position: index + 1 }));
};
export const hasWorseResultQuality = (planned, calculated) => {
    const qualities = [RunsCount.None, RunsCount.Low, RunsCount.Medium, RunsCount.High];
    const plannedIndex = qualities.findIndex((quality) => quality === planned);
    const calculatedIndex = qualities.findIndex((quality) => quality === calculated);
    if (plannedIndex === -1 || calculatedIndex === -1) {
        return false;
    }
    return calculatedIndex < plannedIndex;
};
export const getStatusLabel = ({ status, uncertainty, variability, numberOfOptimisationSteps, calculated, isCompleted, }) => {
    if (!isCompleted || status === CalculationStatusSource.Incomplete) {
        return "Incomplete";
    }
    switch (status) {
        case CalculationStatusSource.InProgress:
            return "In progress...";
        case CalculationStatusSource.Error:
            return "Failed";
        case CalculationStatusSource.Paused:
            return "Paused";
    }
    if (isNotEmpty(calculated)) {
        const { uncertainty: calculatedUncertainty, variability: calculatedVariability, optimizationRuns: calculatedOptimizationRuns, } = calculated;
        if (hasWorseResultQuality(uncertainty, calculatedUncertainty) ||
            hasWorseResultQuality(variability, calculatedVariability) ||
            hasWorseResultQuality(numberOfOptimisationSteps, calculatedOptimizationRuns)) {
            return "Partially available";
        }
    }
    switch (status) {
        case CalculationStatusSource.Canceled:
            return "Canceled";
        case CalculationStatusSource.New:
            return "Not started";
        case CalculationStatusSource.Completed:
            return "Calculated";
        default:
            return "";
    }
};
export const getCompletedSourcesByElement = (element) => element.rows.reduce((acc, { status }) => {
    if (status === CalculationStatusSource.Completed) {
        acc++;
    }
    return acc;
}, 0);
export const getTotalAndCompletedSources = (queue) => queue.elements.reduce((acc, element) => {
    acc.total = acc.total + element.rows.length;
    acc.completed = acc.completed + getCompletedSourcesByElement(element);
    return acc;
}, { total: 0, completed: 0 });
export const updatePredictionTime = (calculation, predictionTimes) => {
    predictionTimes.forEach(({ resultSetId, resultElementId, rows }) => {
        const indexOfElementToUpdate = calculation.elements.findIndex((element) => element.resultElementId === resultElementId && element.resultSetId === resultSetId);
        if (indexOfElementToUpdate >= 0) {
            rows?.forEach(({ id, predictionTime }) => {
                const indexOfRowToUpdate = calculation.elements[indexOfElementToUpdate].rows.findIndex((row) => row.id === id);
                if (indexOfRowToUpdate >= 0) {
                    calculation.elements[indexOfElementToUpdate].rows[indexOfRowToUpdate].predictionTime =
                        predictionTime ?? undefined;
                }
            });
        }
    });
    return calculation;
};
